import {FC, useCallback, useDeferredValue, useMemo, useState} from 'react';
import {styled} from 'styled-components';

import {normalizeString} from '@shared/lib/utils/string_utils';
import {CadViewerPart} from '@shared/models/cad_model';

import {Input} from '@shared_web/components/core/input';
import {Flex} from '@shared_web/components/core2/flex';

import {Translate, translate} from '@mavismart_frontend/components/translate';
import {
  BORDER_COLOR,
  INPUT_THEME,
  LIGHT_GRAY_COLOR,
  MAIN_COLOR,
  TEXT_MAIN_COLOR,
} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';

/** Above this many parts a search field earns its place. */
const SEARCH_THRESHOLD = 8;

interface PartListProps {
  parts: readonly CadViewerPart[];
  selectedPartId: string | undefined;
  hoveredPartId: string | undefined;
  onSelect: (partId: string | undefined) => void;
  onHover: (partId: string | undefined) => void;
}

/**
 * Keyboard-accessible list of the parts in the model.
 *
 * Every row is a real `<button>`, so tabbing and Enter/Space work without any extra key handling,
 * and `aria-pressed` exposes the selection to assistive technology. Selection is shown with an
 * accent bar and bold text as well as colour, so it does not rely on colour alone.
 */
export const PartList: FC<PartListProps> = props => {
  const {parts, selectedPartId, hoveredPartId, onSelect, onHover} = props;
  const account = useAccount();
  const language = getUserLanguage(account);

  const [query, setQuery] = useState('');
  // Typing must never make the list feel sticky when a model has hundreds of parts.
  const deferredQuery = useDeferredValue(query);

  const filtered = useMemo(() => {
    const needle = normalizeString(deferredQuery.trim());
    if (needle === '') {
      return parts;
    }
    return parts.filter(part => {
      const haystack = normalizeString(
        [part.label, part.reference, part.category, part.nodeName].filter(Boolean).join(' ')
      );
      return haystack.includes(needle);
    });
  }, [parts, deferredQuery]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const partId = event.currentTarget.getAttribute('data-part-id');
      if (partId === null) {
        return;
      }
      // Clicking the selected row again clears the selection, matching a click in the void.
      onSelect(partId === selectedPartId ? undefined : partId);
    },
    [onSelect, selectedPartId]
  );

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onHover(event.currentTarget.getAttribute('data-part-id') ?? undefined);
    },
    [onHover]
  );

  const handleMouseLeave = useCallback(() => onHover(undefined), [onHover]);

  // Keyboard users get the same highlight as pointer users.
  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLButtonElement>) => {
      onHover(event.currentTarget.getAttribute('data-part-id') ?? undefined);
    },
    [onHover]
  );

  return (
    <Wrapper $column $gap="s">
      <Header>
        <Translate value="door_3d__parts" />
        <Count>{parts.length}</Count>
      </Header>

      {parts.length > SEARCH_THRESHOLD && (
        <SearchWrapper>
          <Input
            value={query}
            syncState={setQuery}
            themeOverrides={{input: INPUT_THEME}}
            placeholder={translate('door_3d__search_parts', language)}
            aria-label={translate('door_3d__search_parts', language)}
          />
        </SearchWrapper>
      )}

      <List aria-label={translate('door_3d__parts', language)}>
        {filtered.length === 0 ? (
          <Empty>
            <Translate value="door_3d__no_part_found" />
          </Empty>
        ) : (
          filtered.map(part => (
            <Row
              key={part.id}
              type="button"
              data-part-id={part.id}
              $selected={part.id === selectedPartId}
              $hovered={part.id === hoveredPartId && part.id !== selectedPartId}
              aria-pressed={part.id === selectedPartId}
              onClick={handleClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onFocus={handleFocus}
              onBlur={handleMouseLeave}
            >
              <RowLabel $selected={part.id === selectedPartId}>{part.label}</RowLabel>
              {part.category !== undefined && <RowCategory>{part.category}</RowCategory>}
            </Row>
          ))
        )}
      </List>
    </Wrapper>
  );
};

PartList.displayName = 'PartList';

const Wrapper = styled(Flex)`
  min-height: 0;
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${TEXT_MAIN_COLOR};
`;

const SearchWrapper = styled.div`
  width: 100%;

  label {
    font-weight: 400;
    padding-left: 0;
  }

  input {
    width: 100%;
  }
`;

const Count = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: ${TEXT_MAIN_COLOR}aa;
  border: solid 1px ${LIGHT_GRAY_COLOR};
  border-radius: 10px;
  padding: 0 8px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  min-height: 0;
  flex-grow: 1;
`;

const Empty = styled.div`
  padding: 10px 4px;
  font-size: 13px;
  color: ${TEXT_MAIN_COLOR}aa;
`;

const Row = styled.button<{$selected: boolean; $hovered: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  width: 100%;
  text-align: left;
  padding: 7px 10px;
  border: none;
  /* An accent bar carries the selection too, so colour is never the only cue. */
  border-left: solid 3px ${p => (p.$selected ? MAIN_COLOR : 'transparent')};
  border-radius: 0 4px 4px 0;
  background-color: ${p =>
    p.$selected ? `${MAIN_COLOR}14` : p.$hovered ? '#00000010' : 'transparent'};
  color: ${TEXT_MAIN_COLOR};
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background-color: ${p => (p.$selected ? `${MAIN_COLOR}20` : '#00000010')};
  }

  &:focus-visible {
    outline: solid 2px ${BORDER_COLOR};
    outline-offset: -2px;
  }
`;

const RowLabel = styled.span<{$selected: boolean}>`
  font-weight: ${p => (p.$selected ? 700 : 400)};
  overflow-wrap: anywhere;
`;

const RowCategory = styled.span`
  font-size: 11px;
  color: ${TEXT_MAIN_COLOR}99;
`;
